import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { t } from "../i18n/en";
import { type AppNavSection, isAppNavSectionActive } from "../navigation";
import { getAdminAccess } from "../services/admin-service";
import { appQueryKeys, type ChartEditDraft } from "../services/app-query-state";
import {
  clearChartLibrarySession,
  endImpersonation,
  getChartLibrarySession,
  getPrimaryChartForEmail,
} from "../services/chart-library";
import { AppMenu, type AppMenuItem } from "./AppMenu";
import { HarnessWordmark } from "./HarnessWordmark";

type Profile = { username?: string; displayName?: string; isPublic?: boolean };
type ProfileResponse = { profile?: Profile | null } | null;

async function getProfile(session: { token: string }): Promise<Profile | null> {
  const response = await fetch("/api/profile", {
    headers: { Authorization: `Bearer ${session.token}` },
  });
  if (!response.ok) return null;

  const data = (await response.json()) as ProfileResponse;
  return data?.profile || null;
}

export function AppHeader({
  onSignOut,
  onEditProfile,
  editLabel = t("common.editProfile"),
  additionalMenuItems = [],
  activeSection,
  trailingAction,
}: {
  onSignOut?: () => void;
  onEditProfile?: () => void | Promise<void>;
  editLabel?: string;
  additionalMenuItems?: AppMenuItem[];
  activeSection?: AppNavSection | null;
  trailingAction?: ReactNode;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [session] = useState(getChartLibrarySession);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const wasMenuOpen = useRef(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  useEffect(() => {
    if (!session) return () => undefined;
    let active = true;
    void getAdminAccess().then((allowed) => {
      if (active) setIsAdmin(allowed);
    });
    return () => {
      active = false;
    };
  }, [session]);
  useEffect(() => {
    if (!session) return () => undefined;
    let active = true;
    void getProfile(session)
      .then((loadedProfile) => {
        if (active && loadedProfile) setProfile(loadedProfile);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [session]);
  useEffect(() => {
    if (menuOpen) {
      const firstMenuItem = window.setTimeout(() => {
        document.querySelector<HTMLButtonElement>("#app-menu .harness-app-menu-item")?.focus();
      }, 0);
      wasMenuOpen.current = true;
      return () => window.clearTimeout(firstMenuItem);
    }
    if (wasMenuOpen.current) {
      menuTriggerRef.current?.focus();
      wasMenuOpen.current = false;
    }
    return () => undefined;
  }, [menuOpen]);
  useEffect(() => {
    if (!menuOpen) return () => undefined;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [menuOpen]);
  const isSectionActive = (section: AppNavSection) =>
    activeSection === undefined
      ? isAppNavSectionActive(section, pathname)
      : activeSection === section;
  const isMePath = isSectionActive("me");
  const isFriendsPath = isSectionActive("friends");
  const isLearnPath = isSectionActive("learn");
  const isCompatibilityPath = isSectionActive("compatibility");
  const openMyChart = async () => {
    if (!session) {
      void navigate({ to: "/sign-in" });
      return;
    }
    try {
      const navigationProfile = profile || (await getProfile(session));
      if (navigationProfile?.username && navigationProfile.isPublic !== false) {
        void navigate({ to: "/charts/$chartId", params: { chartId: navigationProfile.username } });
        return;
      }

      const ownerChart = await getPrimaryChartForEmail(session.email);
      if (ownerChart) {
        void navigate({ to: "/charts/$chartId", params: { chartId: ownerChart.id } });
        return;
      }
    } catch {
      // Keep the legacy route as a recovery path when the chart cannot be read here.
    }
    void navigate({ to: "/me" });
  };
  const editProfile = async () => {
    if (onEditProfile) {
      await onEditProfile();
      return;
    }
    if (!session) {
      void navigate({ to: "/sign-in" });
      return;
    }
    try {
      const ownerChart = await getPrimaryChartForEmail(session.email);
      if (!ownerChart) {
        void navigate({ to: "/friends" });
        return;
      }
      queryClient.removeQueries({ queryKey: appQueryKeys.wizard() });
      queryClient.setQueryData<ChartEditDraft>(appQueryKeys.editDraft(), {
        id: ownerChart.id,
        email: ownerChart.email,
        name: ownerChart.chartName,
        date: ownerChart.birthDate,
        time: ownerChart.birthTime,
        city: ownerChart.location,
        chartOwner: "self",
        operationalEmailConsent: ownerChart.operationalEmailConsent,
        marketingEmailConsent: ownerChart.marketingEmailConsent,
        emailConsentRecordedAt: ownerChart.emailConsentRecordedAt,
      });
      void navigate({ to: "/wizard" });
    } catch {
      void navigate({ to: "/friends" });
    }
  };
  const signOut = () => {
    if (onSignOut) {
      onSignOut();
      return;
    }
    if (session?.impersonatedBy) endImpersonation();
    clearChartLibrarySession();
    window.location.assign("/");
  };
  const stopImpersonating = () => {
    if (endImpersonation()) {
      window.location.assign("/admin");
      return;
    }
    clearChartLibrarySession();
    window.location.assign("/");
  };
  const primaryNav = [
    {
      id: "me",
      label: t("common.me"),
      active: isMePath,
      onSelect: openMyChart,
    },
    {
      id: "friends",
      label: t("common.friends"),
      active: isFriendsPath,
      onSelect: () => void navigate({ to: "/friends" }),
    },
    {
      id: "learn",
      label: t("common.learn"),
      active: isLearnPath,
      onSelect: () => void navigate({ to: "/learn" }),
    },
    {
      id: "compatibility",
      label: t("common.compatibility"),
      active: isCompatibilityPath,
      onSelect: () => void navigate({ to: "/compatibility-report" }),
    },
  ];
  const accountItems: AppMenuItem[] = [
    ...(onEditProfile
      ? [{ id: "edit", label: editLabel, onSelect: () => void editProfile() }]
      : []),
    ...(isAdmin
      ? [{ id: "admin", label: "Admin", onSelect: () => void navigate({ to: "/admin" }) }]
      : []),
    ...additionalMenuItems,
  ];
  const showAccountMenu = Boolean(session) || accountItems.length > 0;
  return (
    <>
      {session?.impersonatedBy ? (
        <aside
          className="bg-amber-200 px-4 py-2 text-center text-sm font-medium text-black"
          aria-label={t("ui.copy_admin_support_session_186ace46")}
        >
          {t("ui.copy_testing_as_value_for_support_your_se_6c3f2504", {
            email: session.email,
            time: new Date(session.expiresAt).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            }),
          })}
          <button
            type="button"
            className="ml-3 underline underline-offset-2"
            onClick={stopImpersonating}
          >
            {t("ui.copy_return_to_admin_a4af5f8f")}
          </button>
        </aside>
      ) : null}
      <header id="copy-common" className="harness-sticky-header">
        <div className="harness-header-bar">
          <Link to="/" className="harness-header-brand" aria-label={t("accessibility.landingPage")}>
            <HarnessWordmark />
          </Link>
          <nav className="harness-header-nav" aria-label={t("accessibility.primaryNavigation")}>
            {primaryNav.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`harness-header-button ${item.active ? "is-active" : ""}`}
                aria-current={item.active ? "page" : undefined}
                onClick={item.onSelect}
              >
                {item.label}
              </button>
            ))}
          </nav>
          {showAccountMenu ? (
            <div className="harness-header-menu-wrap">
              <button
                type="button"
                ref={menuTriggerRef}
                className={`harness-menu-trigger ${menuOpen ? "is-open" : ""}`}
                aria-expanded={menuOpen}
                aria-controls="app-menu"
                aria-haspopup="menu"
                aria-label={menuOpen ? "Close account menu" : "Account menu"}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span />
                <span />
                <span />
              </button>
              {menuOpen ? (
                <AppMenu
                  id="app-menu"
                  close={() => setMenuOpen(false)}
                  items={accountItems}
                  account={session ? { email: session.email, ...profile } : undefined}
                  onSignOut={session ? signOut : undefined}
                />
              ) : null}
            </div>
          ) : null}
          {trailingAction}
        </div>
      </header>
      <nav className="harness-mobile-bottom-nav" aria-label={t("accessibility.mobileNavigation")}>
        {primaryNav.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={item.onSelect}
            className={item.active ? "is-active" : undefined}
            aria-current={item.active ? "page" : undefined}
          >
            {item.label}
          </button>
        ))}
      </nav>
      <div className="harness-header-spacer" aria-hidden="true" />
    </>
  );
}
